/**
 * `/api/checklist` — the assembled per-day view and the single completion write. See
 * `ChecklistDtos` / `ChecklistController` / `ChecklistService` for the authoritative shapes.
 *
 * There is no bulk "week" endpoint: `GET /api/checklist?date=` always answers for one UTC day (and
 * 400s if that day is in the future). The week grid is built from one query per day of the
 * selected week — `useChecklistWeek` below — which TanStack Query caches independently per date.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiSend, ApiError } from './api';
import { pushToast } from './events';
import { dailyKey } from './period';

export interface ChecklistEntry {
  characterTaskId: number;
  ownerType: 'CHARACTER' | 'ACCOUNT';
  characterId: number | null;
  accountId: number | null;
  source: 'CATALOG' | 'CUSTOM';
  taskDefSlug: string | null;
  customTaskId: number | null;
  name: string;
  cadence: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  scope: 'CHARACTER' | 'ACCOUNT' | 'WORLD';
  maxCount: number;
  note: string | null;
  periodKey: string;
  count: number;
  done: boolean;
  updatedAt: string | null;
}

export interface ChecklistCharacter {
  characterId: number;
  name: string;
  classSlug: string;
  level: number;
  tasks: ChecklistEntry[];
}

export interface ChecklistAccount {
  accountId: number;
  worldSlug: string;
  label: string;
  tasks: ChecklistEntry[];
  characters: ChecklistCharacter[];
}

export interface ChecklistResponse {
  date: string;
  periodKeys: Record<'DAILY' | 'WEEKLY' | 'MONTHLY', string>;
  nextReset: Record<'DAILY' | 'WEEKLY' | 'MONTHLY', string>;
  accounts: ChecklistAccount[];
}

export function checklistQuery(dateIso: string) {
  return {
    queryKey: ['checklist', dateIso] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet<ChecklistResponse>(`/api/checklist?date=${dateIso}`, signal),
  };
}

/** Today's checklist only — what the mobile "today" view and the header countdown need. */
export function useChecklistToday() {
  const today = dailyKey(new Date());
  return useQuery(checklistQuery(today));
}

/**
 * One query per date `<=` today in `dateIsos` (future dates are never requested — the API 400s on
 * them, and there is nothing to check off in a period that has not started). Returns the queries
 * in the same order as `dateIsos`, with `undefined` data for skipped future dates.
 */
export function useChecklistWeek(dateIsos: string[]) {
  const today = dailyKey(new Date());
  const queries = dateIsos.map((iso) => ({
    ...checklistQuery(iso),
    enabled: iso <= today,
  }));
  return useQueries({ queries });
}

/** Deep-replaces one entry's `count`/`done` wherever it appears in a cached response. */
function withEntryUpdated(
  response: ChecklistResponse,
  characterTaskId: number,
  count: number,
): ChecklistResponse {
  const updateEntries = (entries: ChecklistEntry[]): ChecklistEntry[] =>
    entries.map((entry) =>
      entry.characterTaskId === characterTaskId
        ? { ...entry, count, done: count >= entry.maxCount }
        : entry,
    );
  return {
    ...response,
    accounts: response.accounts.map((account) => ({
      ...account,
      tasks: updateEntries(account.tasks),
      characters: account.characters.map((character) => ({
        ...character,
        tasks: updateEntries(character.tasks),
      })),
    })),
  };
}

export interface CompleteTaskVariables {
  characterTaskId: number;
  /** Canonical key of the task's own cadence for the period being written. */
  periodKey: string;
  count: number;
  /**
   * Every currently-cached `?date=` key that could show this attachment — just the one date for a
   * DAILY task, every date of the loaded week for WEEKLY/MONTHLY (they share one completion row).
   * Scopes the optimistic update and rollback precisely instead of invalidating the whole
   * `['checklist']` cache.
   */
  affectedDateIsos: string[];
}

export interface CompletionDto {
  characterTaskId: number;
  cadence: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  periodKey: string;
  count: number;
  maxCount: number;
  done: boolean;
  updatedAt: string;
}

/**
 * `PUT /api/checklist/{id}` with optimistic apply + rollback-on-error, per the WP5 spec. Toggling
 * a checkbox or bumping a counter updates every affected cached day immediately; a failure rolls
 * every one of them back and raises a toast.
 */
export function useCompleteTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: CompleteTaskVariables) =>
      apiSend<CompletionDto>('PUT', `/api/checklist/${vars.characterTaskId}`, {
        periodKey: vars.periodKey,
        count: vars.count,
      }),
    onMutate: async (vars) => {
      const keys = vars.affectedDateIsos.map((iso) => checklistQuery(iso).queryKey);
      await Promise.all(keys.map((key) => queryClient.cancelQueries({ queryKey: key })));
      const previous = keys.map((key) => [key, queryClient.getQueryData<ChecklistResponse>(key)] as const);
      for (const key of keys) {
        queryClient.setQueryData<ChecklistResponse>(key, (old) =>
          old ? withEntryUpdated(old, vars.characterTaskId, vars.count) : old,
        );
      }
      return { previous };
    },
    onError: (error, _vars, context) => {
      context?.previous.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      const message = error instanceof ApiError ? error.serverMessage : 'Could not save that change.';
      pushToast(`${message} Your change was rolled back — try again.`);
    },
    onSettled: (_data, _error, vars) => {
      for (const iso of vars.affectedDateIsos) {
        void queryClient.invalidateQueries({ queryKey: checklistQuery(iso).queryKey });
      }
    },
  });
}
